import React, { useState, useEffect } from 'react';
import { getOwnerToken, getWithToken, postWithToken, delWithToken } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faEnvelope, faTimes, faTrash, faUserPlus, faSync, faClock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const FranquiciadoModal = ({ show, onClose, tiendaId }) => {
    const [franquiciadosRegistrados, setFranquiciadosRegistrados] = useState([]);
    const [franquiciadosPendientes, setFranquiciadosPendientes] = useState([]);
    const [franquiciadoEmail, setFranquiciadoEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const getAdminToken = () => getOwnerToken();

    const normalizeEmail = (email) => email.trim().toLowerCase();

    useEffect(() => {
        if (show) {
            fetchFranquiciados(tiendaId);
        }
    }, [show, tiendaId]);

    const fetchFranquiciados = async (tiendaId) => {
        if (!tiendaId) {
            setFranquiciadosRegistrados([]);
            setFranquiciadosPendientes([]);
            return;
        }

        try {
            setLoading(true);
            setLoadError('');
            let response;
            const token = getAdminToken();

            // Intentar primero el endpoint con estado (incluye registrados y pendientes)
            try {
                response = await getWithToken(`franquiciados/${tiendaId}/franquiciados/with-status`, token);
            } catch (firstError) {
                if (firstError.status === 401 || firstError.status === 403) {
                    throw firstError;
                }

                // Fallback: intentar el endpoint de tiendas (solo registrados)
                try {
                    response = await getWithToken(`tiendas/${tiendaId}/franquiciados`, token);
                    // Si es un array, convertir al formato esperado
                    if (Array.isArray(response)) {
                        response = {
                            registrados: response.map(f => ({
                                _id: f._id,
                                email: f.email,
                                nombre: f.nombre || '',
                                apellido: f.apellido || '',
                                estado: 'registrado'
                            })),
                            pendientes: []
                        };
                    }
                } catch (secondError) {
                    if (secondError.status === 401 || secondError.status === 403) {
                        throw secondError;
                    }

                    // Si ambos fallan, intentar desde el endpoint específico de franquiciados
                    try {
                        response = await getWithToken(`franquiciados/${tiendaId}/franquiciados`, token);
                        if (Array.isArray(response)) {
                            response = {
                                registrados: response.map(f => ({
                                    _id: f._id,
                                    email: f.email,
                                    nombre: f.nombre || '',
                                    apellido: f.apellido || '',
                                    estado: 'registrado'
                                })),
                                pendientes: []
                            };
                        }
                    } catch (thirdError) {
                        throw thirdError;
                    }
                }
            }

            // Procesar la respuesta
            if (response && typeof response === 'object') {
                const registrados = Array.isArray(response.registrados)
                    ? response.registrados.filter(f => f !== null && f !== undefined)
                    : [];
                const pendientes = Array.isArray(response.pendientes)
                    ? response.pendientes.filter(p => p !== null && p !== undefined)
                    : [];

                setFranquiciadosRegistrados(registrados);
                setFranquiciadosPendientes(pendientes);
            } else {
                setFranquiciadosRegistrados([]);
                setFranquiciadosPendientes([]);
            }
        } catch (error) {
            console.error('Error fetching franquiciados:', error);
            setLoadError(
                error.status === 401 || error.status === 403
                    ? 'No se pudieron cargar los franquiciados porque la sesión actual no tiene permisos de propietario. Cierra la sesión de franquiciado y vuelve a entrar como dueño si el problema continúa.'
                    : `No se pudieron cargar los franquiciados. ${error.message || ''}`
            );
            setFranquiciadosRegistrados([]);
            setFranquiciadosPendientes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddFranquiciado = async () => {
        if (!franquiciadoEmail.trim()) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor, ingrese un email válido',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        try {
            setLoading(true);
            const email = normalizeEmail(franquiciadoEmail);
            const result = await postWithToken(`franquiciados/add-to-tienda/${tiendaId}`, { email }, getAdminToken());
            console.log('Franquiciado añadido, respuesta:', result);

            // Limpiar el campo de email
            setFranquiciadoEmail('');

            // Refrescar la lista después de añadir
            await fetchFranquiciados(tiendaId);

            Swal.fire({
                title: '¡Éxito!',
                text: 'Franquiciado añadido o invitación enviada correctamente. Si el franquiciado ya estaba registrado, aparecerá en la lista.',
                icon: 'success',
                confirmButtonText: 'Aceptar',
                timer: 3000
            });
        } catch (error) {
            console.error('Error añadiendo franquiciado:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo agregar el franquiciado',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFranquiciado = async (franquiciadoId, franquiciadoNombre) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas eliminar a ${franquiciadoNombre} de esta tienda?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DC2626',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await delWithToken(`franquiciados/${franquiciadoId}/tiendas/${tiendaId}`, getAdminToken());
                await fetchFranquiciados(tiendaId);
                Swal.fire({
                    title: 'Eliminado',
                    text: 'Franquiciado eliminado correctamente',
                    icon: 'success',
                    confirmButtonText: 'Aceptar',
                    timer: 1500
                });
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message || 'No se pudo eliminar el franquiciado',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleResendInvitation = async (email) => {
        try {
            setLoading(true);
            await postWithToken(`franquiciados/${tiendaId}/resend-invitation`, { email: normalizeEmail(email) }, getAdminToken());
            Swal.fire({
                title: '¡Éxito!',
                text: 'Invitación reenviada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
                timer: 2000
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo reenviar la invitación',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro con blur */}
                <div
                    className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                {/* Contenido del modal */}
                <div
                    className="bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-2xl relative z-10"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header con gradiente púrpura */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 rounded-t-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                    <FontAwesomeIcon icon={faStore} className="text-white text-lg" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Gestionar Franquiciados</h2>
                                    <p className="text-purple-100 text-sm">Añade o elimina franquiciados de esta tienda</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors duration-200 p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                    </div>

                    {/* Contenido del modal */}
                    <div className="px-6 py-6">
                        {/* Formulario para añadir franquiciado */}
                        <div className="bg-purple-50 rounded-lg p-5 mb-6 border border-purple-100">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FontAwesomeIcon icon={faUserPlus} className="text-purple-600 mr-2" />
                                Añadir Nuevo Franquiciado
                            </h3>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleAddFranquiciado();
                                }}
                                className="space-y-4"
                            >
                                <div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                        htmlFor="franquiciadoEmail"
                                    >
                                        <FontAwesomeIcon icon={faEnvelope} className="text-purple-600 mr-2" />
                                        Email del Franquiciado
                                    </label>
                                    <input
                                        type="email"
                                        name="franquiciadoEmail"
                                        value={franquiciadoEmail}
                                        onChange={(e) => setFranquiciadoEmail(e.target.value)}
                                        required
                                        placeholder="franquiciado@ejemplo.com"
                                        disabled={loading}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    />
                                </div>
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={loading}
                                        className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} />
                                        <span>{loading ? 'Añadiendo...' : 'Añadir Franquiciado'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Lista de franquiciados */}
                        <div className="space-y-6">
                            {/* Botón de actualizar */}
                            <div className="flex justify-end">
                                <button
                                    onClick={() => fetchFranquiciados(tiendaId)}
                                    disabled={loading}
                                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Refrescar lista"
                                >
                                    <FontAwesomeIcon icon={faSync} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
                                    <span>Actualizar</span>
                                </button>
                            </div>

                            {loading && franquiciadosRegistrados.length === 0 && franquiciadosPendientes.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                    <p className="text-gray-500 mt-2">Cargando franquiciados...</p>
                                </div>
                            ) : loadError ? (
                                <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
                                    <p className="text-red-700 text-sm font-medium">{loadError}</p>
                                    <button
                                        onClick={() => fetchFranquiciados(tiendaId)}
                                        disabled={loading}
                                        className="mt-4 text-sm text-red-700 hover:text-red-800 font-medium underline disabled:opacity-50"
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Franquiciados Registrados */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                                <FontAwesomeIcon icon={faStore} className="mr-2 text-green-600" />
                                                Franquiciados Registrados ({franquiciadosRegistrados.length})
                                            </h3>
                                        </div>

                                        {franquiciadosRegistrados.length === 0 ? (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                                <FontAwesomeIcon icon={faStore} className="text-3xl text-gray-400 mb-2" />
                                                <p className="text-gray-500 text-sm">No hay franquiciados registrados</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {franquiciadosRegistrados.map((franquiciado) => (
                                                    <div
                                                        key={franquiciado._id}
                                                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 flex items-center justify-between group"
                                                    >
                                                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                                                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                <FontAwesomeIcon icon={faStore} className="text-green-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-gray-900 truncate">
                                                                    {franquiciado.nombre || 'Sin nombre'} {franquiciado.apellido || ''}
                                                                </p>
                                                                <p className="text-sm text-gray-500 truncate flex items-center">
                                                                    <FontAwesomeIcon icon={faEnvelope} className="text-xs mr-1" />
                                                                    {franquiciado.email}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteFranquiciado(franquiciado._id, `${franquiciado.nombre || ''} ${franquiciado.apellido || ''}`.trim() || franquiciado.email)}
                                                            disabled={loading}
                                                            className="ml-4 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors duration-200 font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                            <span className="hidden sm:inline">Eliminar</span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Franquiciados Pendientes */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                                <FontAwesomeIcon icon={faClock} className="mr-2 text-yellow-600" />
                                                Franquiciados Pendientes ({franquiciadosPendientes.length})
                                            </h3>
                                        </div>

                                        {franquiciadosPendientes.length === 0 ? (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                                <FontAwesomeIcon icon={faClock} className="text-3xl text-gray-400 mb-2" />
                                                <p className="text-gray-500 text-sm">No hay invitaciones pendientes</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {franquiciadosPendientes.map((pendiente, index) => (
                                                    <div
                                                        key={`pendiente-${index}-${pendiente.email}`}
                                                        className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 flex items-center justify-between group"
                                                    >
                                                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                                                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-gray-900 truncate flex items-center">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 mr-2">
                                                                        Pendiente
                                                                    </span>
                                                                    {pendiente.email}
                                                                </p>
                                                                <p className="text-sm text-gray-500 mt-1">
                                                                    Esperando registro del franquiciado
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleResendInvitation(pendiente.email)}
                                                            disabled={loading}
                                                            className="ml-4 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors duration-200 font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <FontAwesomeIcon icon={faPaperPlane} />
                                                            <span className="hidden sm:inline">Reenviar</span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Mensaje informativo si no hay nada */}
                                    {franquiciadosRegistrados.length === 0 && franquiciadosPendientes.length === 0 && (
                                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                            <FontAwesomeIcon icon={faStore} className="text-4xl text-gray-400 mb-3" />
                                            <p className="text-gray-500 font-medium">No hay franquiciados asignados</p>
                                            <p className="text-gray-400 text-sm mt-1">Añade franquiciados usando el formulario de arriba</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FranquiciadoModal;

